import { useLocation, useHistory } from 'react-router-dom';
import {useEffect} from 'react';
import {reducer, useSetStateReducer} from '../../../Custom Hooks/useMemorizedReducer/useMemorized.reducer';
import {initializeState} from '../Config/RandomSampler.state';
import {IImageManual} from '../Config/RandomSampler.types';
import {storageService} from '../../../services/storageService';
import {BackendManualRefinementImagesField, BackendTagField} from '../../../configuration/backendFileds/backendFileld.config';
import {request} from '../../../services/HttpRequestService';
import {useSetTimeout} from '../../../Custom Hooks/useSetTimeout/useSetTimeout';
import {_SYSTEM_TITLES} from '../../../configuration/SystemTitles/SystemTitles.config';
import useBowserNavigation from '../../../Custom Hooks/useBowserNavigation/useBowserNavigation';
import {FLOW} from '../Config/RandomSampler.constants';

const useRandomSampler = (flow = '', setClassesForRandomPage = () => {}, autoProjectState = {classes: [], projectId: -1}) => {
    const location = useLocation();
    const history = useHistory();
    const { state } = location;
    // eslint-disable-next-line prefer-const
    let { projectIdInit, classes } = state || {};
    const [getState, setState] = useSetStateReducer(reducer, initializeState);
    const randomSamplerState = getState();
    const {_setTimeOut} = useSetTimeout();

    /**
     * when user wants to go back to objects tab
     */
    const goBackToObjectsTab = () => {
        if (!randomSamplerState.globalLoading) {
            storageService.removeFromStorage('randomSamplerIndex');
            history.push({
                pathname: `/${_SYSTEM_TITLES.classInventoryTabName}`,
                state: {},
            });
        }
    };

    useBowserNavigation(goBackToObjectsTab, null, null, true);

    /**
     * setManualImages - setManualImages
     * @param images - manual Images
     */
    const setManualImages = (images) => {
        const newImages:IImageManual[] = [];

        images.forEach((img) => {
            newImages.push({
                title: img[BackendManualRefinementImagesField.name],
                fileServerId: BackendManualRefinementImagesField.fileServerId in img ? img[BackendManualRefinementImagesField.fileServerId] : null,
                url: img[BackendManualRefinementImagesField.url],
                approved: false,
                tagId: -1,
                disable: false,
                disableTitle: '',
                isSendToDelete: false,
                isLoadFromOutSide: false,
                newFileServerId: -1
            });
        });

        return newImages;
    };

    /**
     * setManualImagesForDb - setManualImagesForDb
     * @param images - manual Images
     * @param tagName - tag name
     */
    const setManualImagesForDb = (images, tagName) => {
        const newImages: any = [];

        images.forEach((img) => {
            newImages.push({
                [BackendManualRefinementImagesField.name]: img.title,
                [BackendManualRefinementImagesField.fileServerId]: img.fileServerId,
                [BackendTagField.id]: img.tagId,
                [BackendTagField.name]: tagName
            });
        });

        return newImages;
    };

    /**
     * addImageToClassReference - send to 'copy_random_images/ API
     * @param images - images that need to copy
     * @param copyManualImages - UI images to update
     * @param copyClasses - UI classes to update
     */
    const addImageToClassReference = async (images, copyManualImages, copyClasses) => {
        try {
            const req = {
                images_list: setManualImagesForDb(images, copyClasses[randomSamplerState.classIndex].name),
                shared_data_project_id: projectIdInit ? projectIdInit : autoProjectState.projectId,
                batch_id: randomSamplerState.batchId
            };

            const res = await request.post(storageService.loadFromStorage('token'), 'COPY_RANDOM_IMAGES', req);
            const filesIds = res.data.file_client_list;
            const new_tag_id = res.data.new_tag_id;
            const isRandomBtn = res.data.is_random_images;

            copyClasses[randomSamplerState.classIndex].isShowAddToClassSuccess = true;
            !copyClasses[randomSamplerState.classIndex].tagId && (copyClasses[randomSamplerState.classIndex].tagId = new_tag_id);
            copyManualImages.forEach((img) => filesIds[img.title] && (img.newFileServerId = filesIds[img.title]));
            setState({isCleanClick: true, manualImages: copyManualImages, classes: copyClasses, isRandomBtn});

        } catch (e) {
            copyClasses[randomSamplerState.classIndex].isShowAddToClassError = true;
            setState({isCleanClick: true, manualImages: copyManualImages, classes: copyClasses});
        }
    };

    /**
     * disableDelete - when user press on remove from class after adding image
     * @param imageNumber - image number
     */
    const disableDelete = (imageNumber) => {
        const copyManualImages = JSON.parse(JSON.stringify(randomSamplerState.manualImages));

        copyManualImages[imageNumber].isSendToDelete = true;
        setState({manualImages: copyManualImages, imageNumber, globalLoading: true});
    };

    /**
     * sendGetRandomSamplerToServer - get random images from server get_random_sniffer_images/ API
     */
    const sendGetRandomSamplerToServer = async (dataSource, dataTime, copyClasses = []) => {
        const res = await request.getMultiParam(storageService.loadFromStorage('token'), 'GET_RANDOM_IMAGES', false, [dataSource, dataTime]);
        const images = res.data.res_images;
        const isRandomBtn = res.data.is_random_images;
        const batchId = res.data.batch_id;
        const manualImages = JSON.parse(JSON.stringify(setManualImages(images)));
        const isEmpty = manualImages.length === 0;

        const newState = {
            manualImages,
            isCleanClick: false,
            isRandomBtn,
            batchId,
            isEmpty
        };

        if (copyClasses.length > 0) {
            newState['classes'] = copyClasses;
        }
        setState(newState);
    };

    /**
     * getAvailableDataSources - get available data sources for the account
     */
    const getAvailableDataSources = async () => {

        const res = await request.get(storageService.loadFromStorage('token'), 'GET_SOURCES_WITH_DATA', false, '');
        const availableSources = res.data.available_sources;
        const newState = {
            availableSources: availableSources
        };

        const defaultSource = 'liveSessions';

        if (!availableSources.includes(defaultSource)) {
            newState['dataSource'] = availableSources[0];
        } else {
            newState['dataSource'] = defaultSource;
        }
        setState(newState);
    };

    /**
     * getRandomSamplerData - get data from Server
     */
    const getRandomSamplerData = async (dataSource = '', dataTime = '', outside_classes = []) => {

        if (!randomSamplerState.globalLoading) {
            try {
                setState({isEmpty: false});
                const copyManualImages = JSON.parse(JSON.stringify(randomSamplerState.manualImages));
                let copyClasses = [] as any[];

                if (outside_classes.length > 0) {
                    copyClasses = JSON.parse(JSON.stringify(outside_classes));
                } else {
                    copyClasses = JSON.parse(JSON.stringify(randomSamplerState.classes));
                }

                if (copyManualImages.length > 0) {
                    copyManualImages.forEach((img) => {
                        img.url = '';
                        img.isLoadFromOutSide = true;
                        img.approved = false;
                        img.disableTitle = '';
                        img.disable = false;
                    });

                    copyClasses.forEach((cl) => {
                        cl.isChosen = false;
                    });
                    setState({manualImages: copyManualImages, isCleanClick: true, classes: copyClasses});

                    _setTimeOut(() => {
                        sendGetRandomSamplerToServer(dataSource, dataTime, copyClasses as any);
                    }, 1000);
                } else {
                    await sendGetRandomSamplerToServer(dataSource, dataTime, copyClasses as any);
                }

            } catch (e) {
                setState({isErrorPage: true});
            }
        }
    };

    /**
     * removeImageFromClass - calling remove_random_image/ API
     */
    const removeImageFromClass = async () => {
        const copyManualImages = JSON.parse(JSON.stringify(randomSamplerState.manualImages));

        if (randomSamplerState.imageNumber > -1 && copyManualImages[randomSamplerState.imageNumber].isSendToDelete) {
            try {
                const req = {
                    file_id: copyManualImages[randomSamplerState.imageNumber].newFileServerId,
                    sniffer_file_id: copyManualImages[randomSamplerState.imageNumber].fileServerId,
                };

                const res = await request.post(storageService.loadFromStorage('token'), 'REMOVE_RANDOM_IMAGE', req);
                const isRandomBtn = res.data.is_random_images;

                copyManualImages[randomSamplerState.imageNumber].isSendToDelete = false;
                copyManualImages[randomSamplerState.imageNumber].tagId = -1;
                copyManualImages[randomSamplerState.imageNumber].disable = false;
                copyManualImages[randomSamplerState.imageNumber].disableTitle = '';
                copyManualImages[randomSamplerState.imageNumber].newFileServerId = -1;
                setState({imageNumber: -1, manualImages: copyManualImages, globalLoading: false, isRandomBtn});
            } catch (e) {
                copyManualImages[randomSamplerState.imageNumber].isSendToDelete = false;
                setState({imageNumber: -1, manualImages: copyManualImages, globalLoading: false});
            }
        }
    };

    useEffect(() => {
        (async () => {
            if (flow === FLOW.randomImages) {
                await getAvailableDataSources();
            } else if (flow === FLOW.autoClasses) {
                if ((randomSamplerState.batchId > -1 && randomSamplerState.isDoneBatch === true) || randomSamplerState.batchId === -1) {
                    const outside_classes = setClassesForRandomPage();

                    await getRandomSamplerData('', '', outside_classes as any);
                    setState({isDoneBatch: false, isImagesLoading: false});
                }
            }
        })();
    }, [randomSamplerState.isDoneBatch]);


    useEffect(() => {
        setState({classes: classes});
        (async () => {
            if (randomSamplerState.availableSources.length > 0) {
                await getRandomSamplerData(randomSamplerState.dataSource, randomSamplerState.dataTime);
            }
        })();
    }, [randomSamplerState.availableSources]);

    useEffect(() => {
        (async () => {
            randomSamplerState.imageNumber > -1 && await removeImageFromClass();
        })();

    }, [randomSamplerState.imageNumber]);


    return {goBackToObjectsTab, randomSamplerState, setState, getRandomSamplerData, disableDelete, addImageToClassReference};
};

export default useRandomSampler;
